import GrabIcon from '@svg/grab.svg';
import React, { useCallback, useEffect, useRef } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableChildrenFn,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableStateSnapshot,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { createPortal } from 'react-dom';
import {
  DragHandle,
  DraggableElement,
  Element,
  List,
  ListElement,
} from './styled';

export type OnOrderChange = (ids: string[]) => void;
export type ReorderableElement = {
  id: string;
  component: React.ReactChild;
};

function moveItemInArray<T>(
  array: T[],
  oldIndex: number,
  newIndex: number,
): T[] {
  const item = array[oldIndex] as T;
  const rearrangedArray = Array.from(array);
  rearrangedArray.splice(oldIndex, 1);
  rearrangedArray.splice(newIndex, 0, item);

  return rearrangedArray;
}

function DraggableItem({
  dragHandleProps,
  isDraggingThisItem,
  isDraggingOverThisList,
  children,
}: {
  isDraggingThisItem: DraggableStateSnapshot['isDragging'];
  isDraggingOverThisList: DroppableStateSnapshot['isDraggingOver'];
  children: React.ReactChild;
  dragHandleProps: DraggableProvided['dragHandleProps'];
}) {
  return (
    <DraggableElement
      $isDraggingThisItem={isDraggingThisItem}
      $isDraggingOverThisList={isDraggingOverThisList}
    >
      <DragHandle aria-label="Drag handle" {...dragHandleProps}>
        <GrabIcon />
      </DragHandle>
      <Element>{children}</Element>
    </DraggableElement>
  );
}

// From: https://stackoverflow.com/questions/73427078/react-beautiful-dnd-how-to-fix-list-items-sliding-down-on-isdraggingover
const useDraggableInPortal = (usePortal: boolean) => {
  const self = useRef<{ elt?: HTMLDivElement }>({}).current;

  useEffect(() => {
    if (!usePortal) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.pointerEvents = 'none';
    div.style.top = '0';
    div.style.width = '100%';
    div.style.height = '100%';
    self.elt = div;
    document.body.appendChild(div);
    return () => {
      document.body.removeChild(div);
    };
  }, [self, usePortal]);

  return (render: DraggableChildrenFn): DraggableChildrenFn =>
    (provided, ...args) => {
      const element = render(provided, ...args);
      if (
        usePortal &&
        provided.draggableProps?.style &&
        'position' in provided.draggableProps.style &&
        provided.draggableProps.style.position === 'fixed' &&
        self.elt
      ) {
        return createPortal(element, self.elt);
      }
      return element;
    };
};

interface ReorderableListProps {
  orderedElements: ReorderableElement[];
  onOrderChange: OnOrderChange;
  /** when there are multiple reorderable lists on the page, each needs a unique id */
  uniqueDragListId?: string;
  listAriaLabel: string;
  disableDragging?: boolean;
  /**
   * If true, the draggable element will be rendered in a portal.
   * This allows draggable items to be positioned correctly
   * when the reorderable list is inside a menu or modal container.
   */
  usePortal?: boolean;
}

export function ReorderableList({
  orderedElements,
  onOrderChange,
  uniqueDragListId = 'reorderable-fields',
  listAriaLabel,
  disableDragging,
  usePortal,
}: ReorderableListProps) {
  const renderDraggable = useDraggableInPortal(!!usePortal);
  const handleDragEnd: OnDragEndResponder = useCallback(
    (result) => {
      const { destination, source } = result;
      if (!destination) {
        return; // drop got cancelled or dropped outside of drop-area
      }

      const [oldIndex, newIndex] = [source.index, destination.index];

      if (oldIndex === newIndex) {
        return; // got dropped in same place again
      }

      const oldOrder = orderedElements.map((element) => element.id);
      const newOrder = moveItemInArray(oldOrder, oldIndex, newIndex);

      onOrderChange(newOrder);
    },
    [orderedElements, onOrderChange],
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={uniqueDragListId}>
        {(dropProvided, dropSnapshot) => (
          <List
            {...dropProvided.droppableProps}
            ref={dropProvided.innerRef}
            aria-label={listAriaLabel}
          >
            {orderedElements.map((element, index) => (
              <ListElement key={element.id}>
                <Draggable
                  draggableId={element.id}
                  index={index}
                  isDragDisabled={disableDragging}
                >
                  {renderDraggable(
                    (
                      dragProvided: DraggableProvided,
                      dragSnapshot: DraggableStateSnapshot,
                    ) => (
                      <div
                        {...dragProvided.draggableProps}
                        ref={dragProvided.innerRef}
                      >
                        <DraggableItem
                          isDraggingThisItem={dragSnapshot.isDragging}
                          isDraggingOverThisList={dropSnapshot.isDraggingOver}
                          dragHandleProps={dragProvided.dragHandleProps}
                        >
                          {element.component}
                        </DraggableItem>
                      </div>
                    ),
                  )}
                </Draggable>
              </ListElement>
            ))}
            {dropProvided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
}
